import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Radio, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import EnquiryModal from "@/components/EnquiryModal";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [platform, setPlatform] = useState({ legal_company_name: "", logo_url: "" });

  // Pull the platform profile so the brand mark stays in sync with whatever
  // the team configures in /app/brand-settings — no redeploy required.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/platform-profile");
        setPlatform(data.platform_profile || {});
      } catch (e) { console.debug("Platform profile lookup failed:", e); }
    })();
  }, []);

  const brandName = platform.legal_company_name?.trim() || "Vanguard Beacon";
  const brandLogo = platform.logo_url || null;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (!r.ok) return setErr(r.error || "Login failed");
    if (r.user?.must_reset_password) nav("/change-password");
    else if (r.user?.role === "staff") nav("/app/daily-tracking");
    else nav("/app");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 vb-fade-in" data-testid="login-page">
      {/* Visual side */}
      <div className="hidden lg:flex flex-col justify-between p-14 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #2f2e2c 0%, #783D19 100%)" }}>
        <div className="flex items-center gap-3 text-white" data-testid="login-brand">
          <div className="flex items-center justify-center overflow-hidden" style={{ width: 48, height: 48 }}>
            {brandLogo
              ? <img src={brandLogo} alt={brandName} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} data-testid="login-brand-logo" />
              : <Radio size={26} color="#e2c8b5" data-testid="login-brand-logo-fallback" />}
          </div>
          <span className="vb-heading text-2xl font-bold" data-testid="login-brand-name">{brandName}</span>
        </div>
        <div className="text-white max-w-md">
          <h1 className="vb-heading text-5xl font-bold leading-tight" style={{ color: "var(--vb-bg)" }}>
            Audit-Ready, <em style={{ color: "#e2c8b5" }}>Every Day.</em>
          </h1>
          <p className="mt-6 text-base opacity-85">
            Capture, verify, and resolve every critical control point in real time—automatically.
            Built for multi-site operators who don't have time for audit surprises.
          </p>
          <div className="mt-8 flex gap-3">
            <span className="vb-pill" style={{ background: "#e2c8b5", color: "#783D19" }}>HACCP-aligned</span>
            <span className="vb-pill" style={{ background: "#e2c8b5", color: "#783D19" }}>Multi-site</span>
            <span className="vb-pill" style={{ background: "#e2c8b5", color: "#783D19" }}>AI-assisted CAPA</span>
          </div>
        </div>
        <p className="text-xs opacity-60" style={{ color: "var(--vb-bg)" }}>© Vanguard Beacon — Audit-ready food safety</p>
      </div>

      {/* Form side */}
      <div className="flex flex-col items-center justify-center p-8 gap-4" style={{ background: "var(--vb-bg)" }}>
        <form onSubmit={submit} className="w-full max-w-md vb-card p-8" data-testid="login-form">
          <h2 className="vb-heading text-3xl mb-1" style={{ color: "var(--vb-text-2)" }}>Welcome back</h2>
          <p className="opacity-70 mb-6 text-sm">Sign in to your Vanguard Beacon workspace</p>

          {err && <div className="vb-pill err mb-4 w-full" data-testid="login-error">{err}</div>}

          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Work email</label>
          <div className="mb-4">
            <input data-testid="login-email" type="text" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="vb-input" placeholder="you@company.com" autoComplete="username" />
          </div>

          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Password</label>
          <div className="mb-6">
            <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="vb-input" placeholder="••••••••" />
          </div>

          <button data-testid="login-submit" type="submit" className="vb-btn w-full" disabled={busy}>
            {busy ? "Signing in…" : "Access Dashboard"}
          </button>

          <div className="mt-3 text-sm text-center">
            <Link to="/forgot-password" className="vb-link" data-testid="forgot-link">Reset Password</Link>
          </div>

          <div className="mt-6 pt-5 border-t text-sm text-center space-y-2" style={{ borderColor: "var(--vb-border)" }}>
            <div>
              Setting up a new organization?{" "}
              <Link to="/first-login" className="vb-link font-semibold" data-testid="first-login-link">Start here</Link>
            </div>
          </div>
        </form>

        {/* Enquiry CTA — separated so existing users won't tap it accidentally */}
        <button
          type="button"
          onClick={() => setShowEnquiry(true)}
          className="vb-link inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100 mt-2"
          data-testid="enquire-link"
        >
          <MessageCircle size={12} /> Not a customer yet? Enquire about our services
        </button>
      </div>
      {showEnquiry && <EnquiryModal onClose={() => setShowEnquiry(false)} />}
    </div>
  );
}
